import { Typography } from '@mui/material';
import { Column, Label, Section, Text } from './TravelDetailStyles';
import { formatDate } from 'utils/date';
import {
  calculateAdjustedDistanceInMeters,
  calculateManualDistanceInMeters,
  formatDistance
} from '../utils';
import { useTranslation } from 'react-i18next';
import type {
  ExpenseClaimResponse,
  TravelMovementPerDayResponse,
  TravelMovementResponse
} from 'generated/index';
import {
  calculateCompensationInEuros,
  calculateDistancesByType,
  generateDistanceText,
  sumInEuros
} from '../utils';
import { rgbaOrange } from 'constants/colors';
import { Icon } from '@sdb/planning-ui';

import type { ClaimResponse, RouteResponse } from '../types';

type Props = {
  claim: ClaimResponse;
  routes: RouteResponse;
  isTravelMovements?: boolean;
  isManual?: boolean;
  isCorrected?: boolean;
  isMismatch?: boolean;
  isCredited?: boolean;
  isRegisterPrivateKilometers?: boolean;
};

const TravelDetailDialogOverview = ({
  claim,
  routes,
  isTravelMovements,
  isManual,
  isCorrected,
  isMismatch,
  isCredited,
  isRegisterPrivateKilometers
}: Props) => {
  const { t } = useTranslation('common');

  const isTravelMovementArray = (
    routes: RouteResponse
  ): routes is TravelMovementResponse[] => Array.isArray(routes);

  // Calculate distances
  const manualDistanceInMeters =
    isManual && isTravelMovementArray(routes)
      ? calculateManualDistanceInMeters(routes)
      : null;

  const adjustedDistanceInMeters =
    isManual && isTravelMovementArray(routes)
      ? calculateAdjustedDistanceInMeters(
          routes,
          'commuteDistanceInMeters' in claim
            ? claim.commuteDistanceInMeters || 0
            : 0
        )
      : null;

  const distancesByType = isTravelMovementArray(routes)
    ? calculateDistancesByType(
        routes,
        !!isTravelMovements || !!isRegisterPrivateKilometers
      )
    : { private: 0, business: 0 };

  // Generate distance text
  const distanceText = generateDistanceText(
    distancesByType,
    !!isManual,
    manualDistanceInMeters,
    isTravelMovements,
    isRegisterPrivateKilometers,
    t
  );

  // Calculate compensationInEuros and round to 2 decimal places
  const compensationInEuros = isTravelMovementArray(routes)
    ? calculateCompensationInEuros(routes, isTravelMovements)
    : 0;

  const hasTransportationMode = (
    claim:
      | ExpenseClaimResponse
      | TravelMovementResponse
      | TravelMovementPerDayResponse
  ): claim is TravelMovementResponse => {
    return 'transportationMode' in claim;
  };

  const commuteDistanceInMeters = (claim as TravelMovementPerDayResponse)
    ?.commuteDistanceInMeters;

  return (
    <>
      <Typography variant="h6">{t('travelExpenseClaims.claim')}</Typography>
      <Section>
        <Column>
          <Label>{t('date')}</Label>
          <Text>{formatDate(claim.date)}</Text>
        </Column>

        {(!isTravelMovements || isManual) && !isRegisterPrivateKilometers && (
          <Column>
            <Label>{t('travelExpenseClaims.subtractCommuteDistance')}</Label>
            <Text>{t(claim.subtractCommuteDistance ? 'yes' : 'no')}</Text>
          </Column>
        )}

        {(!isTravelMovements || isManual) && isRegisterPrivateKilometers && (
          <Column>
            <Label>{t('travelExpenseClaims.registerPrivateKilometers')}</Label>
            <Text>{t(claim.registerPrivateKilometers ? 'yes' : 'no')}</Text>
          </Column>
        )}

        <Column>
          <Label>{t('status')}</Label>
          <Text>
            {t(
              `${
                isTravelMovements ? 'travelMovements' : 'travelExpenseClaims'
              }.status.${claim.status}`
            )}
          </Text>
        </Column>

        <Column>
          <Label>{t('travelExpenseClaims.distance')}</Label>
          <Text>
            {isManual
              ? `${formatDistance(adjustedDistanceInMeters || 0)}`
              : distanceText}
          </Text>
        </Column>

        {isManual && (
          <Column>
            <Label>{t('travelMovements.commutedDistance')}</Label>
            <Text>
              {t(
                commuteDistanceInMeters
                  ? formatDistance(commuteDistanceInMeters)
                  : '-'
              )}
            </Text>
          </Column>
        )}

        <Column>
          <Label>{t('travelDistances.transportationMode')}</Label>
          <Text>
            {isTravelMovements
              ? hasTransportationMode(claim)
                ? t(
                    `travelDistances.transportationModes.${claim.transportationMode}`
                  )
                : ''
              : 'transportationType' in claim
              ? claim.transportationType?.name ?? ''
              : ''}
          </Text>
        </Column>

        {isTravelMovements && !isManual && (
          <Column>
            <Label>{t('travelDistances.compensation')}</Label>
            <Text>
              {t(compensationInEuros ? sumInEuros(compensationInEuros) : '-')}
            </Text>
          </Column>
        )}

        {isTravelMovements && isCorrected && (
          <Column>
            <Label>{t('travelMovements.corrected')}</Label>
            <Text>{t(isCorrected ? 'yes' : 'no')}</Text>
          </Column>
        )}

        {(!isTravelMovements || isManual) && (
          <>
            <Column>
              <Label>
                {t(
                  isTravelMovements
                    ? 'travelMovements.declarationType'
                    : 'travelExpenseClaims.declarationType'
                )}
              </Label>
              <Text>{claim?.declarationType?.name ?? '-'}</Text>
            </Column>
            <Column
              sx={{
                gridColumn: 'span 3'
              }}
            >
              <Label>{t('description')}</Label>
              <Text>{claim.description}</Text>
            </Column>
          </>
        )}

        {isTravelMovements && isMismatch && !isCredited && (
          <Column
            sx={{
              gridColumn: 'span 3',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              m: '0 -10px -10px',
              p: '8px 10px',
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
              bgcolor: rgbaOrange(0.08)
            }}
          >
            <Icon
              type="triangle-exclamation"
              color="warning"
              fontSize="small"
            />
            <Text>{t('travelMovements.misMatch')}</Text>
          </Column>
        )}
      </Section>
    </>
  );
};

export default TravelDetailDialogOverview;
